/**
 * A checkbox-list filter control: search, select all, clear, multi-select.
 *
 * Matches the Power BI slicers the report is replacing — a closed control that reads
 * "All" or names what is picked, opening to a searchable checkbox list.
 *
 * Two house rules apply here and are easy to lose:
 *
 *  - Nothing is de-emphasised with `opacity`. It fades text below AA *and* hides from
 *    a contrast audit, because an ancestor's opacity never appears on a child's
 *    computed style. Muted text is `--ink-3`, which clears AA on every ground in this
 *    app; an unavailable state is carried by a dashed rule and a stated reason.
 *  - An empty selection means ALL, not none. Clearing the last checkbox returns the
 *    control to "All" rather than emptying every visual on the page.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { MODELLED_ALL } from '@/data/claimCells'
import { matches } from '@/ui/match'
import type { Definition } from '@/measures/definitions'
import { Info } from './Info'

export interface MultiSelectProps {
  label: string
  /** Every value the data offers. */
  options: string[]
  /** Selected values. EMPTY = all. */
  value: string[]
  onToggle: (v: string) => void
  onSet: (vs: string[]) => void
  /** Marks a filter whose result is an allocation rather than a count. */
  modeled?: boolean
  /** The M's hover: what the filter models. `MODELED_FILTER_NOTE` when not given. */
  modeledNote?: string
  /** A muted qualifier after the label. */
  hint?: string
  /** Tooltip on the whole control. */
  title?: string
  /** What the filter is, as the "i" beside its name. */
  help?: Definition
  /** Show the search box. Default: once the list is long enough to need it. */
  searchable?: boolean
  /** How a value reads, where it is a key: '2026-05' as "May 2026". Default: as it is. */
  labelOf?: (v: string) => string
  /** What an empty selection reads as, where it is not every option. Default "All". */
  allLabel?: string
}

/**
 * What the M on a claim-grain filter means on a bundle without the claim cells. Since
 * the 2026-10-07 bundle the cells make those filters exact and the M is gone, except
 * for weighted claims (Shayna's review #16; data/claimCells.ts, `claimFilterNote`).
 */
export const MODELED_FILTER_NOTE = MODELLED_ALL

const control: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 7px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  minWidth: 0,
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
}

export function MultiSelect({
  label,
  options,
  value,
  onToggle,
  onSet,
  modeled = false,
  modeledNote = MODELED_FILTER_NOTE,
  hint,
  title,
  help,
  searchable,
  labelOf = (v) => v,
  allLabel = 'All',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const box = useRef<HTMLDivElement>(null)
  const listId = useId()

  const showSearch = searchable ?? options.length > 8

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Words in any order, as SearchPick matches (ui/match.ts).
  const shown = useMemo(
    () => (q.trim() ? options.filter((o) => matches(labelOf(o), q)) : options),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, q],
  )

  const allSelected = value.length === 0

  // What the closed control says. Naming one or two picks beats "2 selected", which
  // makes a reader open the control to find out what they already chose.
  const summary =
    value.length === 0
      ? allLabel
      : value.length <= 2
        ? value.map(labelOf).join(', ')
        : `${value.length} of ${options.length}`

  return (
    <div
      ref={box}
      style={{ display: 'grid', gap: 4, position: 'relative' }}
      title={title}
      // Esc closes the list and stops there, so an enlarged table (PopOut) around the
      // filter stays open. The document listener above covers focus outside the box.
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.preventDefault()
          setOpen(false)
        }
      }}
    >
      <span className="eyebrow">
        {label}
        {hint && <span style={{ color: 'var(--ink-3)' }}> {hint}</span>}
        <Info def={help} />
        {modeled && (
          <span
            className="modeled"
            style={{ marginLeft: 6, verticalAlign: 0 }}
            title={modeledNote}
          >
            M
          </span>
        )}
      </span>

      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setQ('')
            setOpen(true)
          } else if (
            showSearch &&
            e.key.length === 1 &&
            /\S/.test(e.key) &&
            !e.ctrlKey &&
            !e.metaKey &&
            !e.altKey
          ) {
            // A letter on the closed control opens it already searching, as SearchPick
            // does (review #403: "search bars you can type into").
            e.preventDefault()
            setQ(e.key)
            setOpen(true)
          }
        }}
        style={{
          ...control,
          borderColor: allSelected ? 'var(--rule-2)' : 'var(--accent)',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: allSelected ? 'var(--ink-3)' : 'var(--ink)',
          }}
        >
          {summary}
        </span>
        <span className="mono" style={{ marginLeft: 'auto', color: 'var(--ink-3)' }} aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          aria-label={label}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 30,
            minWidth: '100%',
            maxWidth: 320,
            marginTop: 4,
            background: 'var(--surface)',
            border: '1px solid var(--rule-2)',
            borderRadius: 7,
            boxShadow: 'var(--shadow)',
            padding: 6,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {showSearch && (
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              aria-label={`Search ${label}`}
              style={{
                ...control,
                cursor: 'text',
                display: 'block',
                marginBottom: 6,
              }}
            />
          )}

          <label
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              padding: '4px 5px',
              cursor: 'pointer',
              borderBottom: '1px solid var(--rule)',
              marginBottom: 4,
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              // Selecting "Select all" clears the selection, because empty IS all here.
              // Writing out every value instead would look identical and then silently
              // exclude anything the next bundle adds.
              onChange={() => onSet(allSelected ? shown.slice() : [])}
            />
            <span style={{ fontSize: 'var(--t-table)' }}>{allLabel === 'All' ? 'Select all' : allLabel}</span>
          </label>

          {shown.length === 0 && (
            <p
              style={{
                fontSize: 'var(--t-table)',
                color: 'var(--ink-3)',
                margin: '6px 5px',
              }}
            >
              Nothing matches “{q}”.
            </p>
          )}

          {shown.map((o) => (
            <label
              key={o}
              role="option"
              aria-selected={value.includes(o)}
              style={{
                display: 'flex',
                gap: 7,
                alignItems: 'center',
                padding: '4px 5px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={value.includes(o)}
                onChange={() => onToggle(o)}
              />
              <span
                style={{
                  fontSize: 'var(--t-table)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {labelOf(o)}
              </span>
            </label>
          ))}

          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onSet([])}
              style={{
                ...control,
                marginTop: 6,
                justifyContent: 'center',
                color: 'var(--accent-ink)',
              }}
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  )
}
